import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormGroup, UntypedFormGroup} from "@angular/forms";
import {Transport, TransportType} from "@core/shared/models/transport";
import {SelectSearchConfiguration} from "@core/components/select/select-search/select-search.component";
import {Observable} from "rxjs";
import {TransportService} from "@core/shared/services/transport.service";
import {map} from "rxjs/operators";
import {Pagination} from "@core/shared/models/pagination";
import {OfferLocationDirection} from "@core/shared/models/offer/offer-location";
import {AdditionalHeader} from "@core/shared/models/http-client";
import {DEACTIVATE_LOADER_HEADER_IDENTIFIER} from "@app/data";

@Component({
    selector: 'app-core-offer-location-transport-select-search',
    templateUrl: './offer-location-transport-select-search.component.html',
    styleUrl: './offer-location-transport-select-search.component.scss',
    standalone: false
})
export class OfferLocationTransportSelectSearchComponent implements OnInit {

    @Input() offerLocationControl: UntypedFormGroup;

    @Input() direction: OfferLocationDirection;

    @Input() type: TransportType;

    public offerLocationTransportControl: FormGroup;

    public latitude: number;

    public longitude: number;

    public searchConfiguration: SelectSearchConfiguration<Transport>;

    public searchSourceCallback: (search: string) => Observable<Transport[]>;

    constructor(
        private _transportService: TransportService
    ) {}

    ngOnInit() {

        this._initOfferLocationTransportControl();

        this._initCoordinates();

        this._initConfiguration();
    }

    private _initOfferLocationTransportControl(): void {

        const offerLocationTransportsControl: FormArray = this.offerLocationControl.get('transports') as FormArray;

        this.offerLocationTransportControl = offerLocationTransportsControl.controls.find((control: FormGroup): boolean => {

            if((control.get('transport').value.type !== this.type)){

                return false;
            }

            switch (this.direction){

                case OfferLocationDirection.Arrival:

                    return control.get(OfferLocationDirection.Arrival).value;

                case OfferLocationDirection.Departure:

                    return control.get(OfferLocationDirection.Departure).value;

                default:

                    return false;
            }

        }) as FormGroup;
    }

    private _initCoordinates(): void {

        this.latitude = this.offerLocationControl.get('latitude').value;

        this.longitude = this.offerLocationControl.get('longitude').value;
    }

    private _initConfiguration(): void {

        this.searchConfiguration = {
            multiple: false,
            enableSearch: true,
            displaySelection: false,
            currentSelectionLabel: 'selectSearch.selection.current.value',
            searchActionLabel: 'selectSearch.search.action.value',
            selectOptionActionLabel: this.selectOptionActionLabel,
            itemLabel: (item: Transport): string => {

                return `${item.label}`;
            },
            postSourceConsumeCallback: (items: Transport[]): void => {

                if(!items.length){

                    return;
                }

                const currentTransport: Transport =  this.offerLocationTransportControl.get('transport').value;

                if(this.offerLocationTransportControl.get('transport').value && this.offerLocationTransportControl.get('transport').value.id){

                    const match: Transport = items.find((item: Transport): boolean => {

                        return item.id === currentTransport.id;
                    });

                    if(match){

                        return;
                    }
                }

                this.offerLocationTransportControl.get('transport').patchValue(items[0]);
            }
        }

        this.searchSourceCallback = (search: string): Observable<Transport[]> => {

            const params: string[] = [
                `page=1`,
                'limit=10',
                `type[eq]=${this.type}`,
                `sort[distance][latitude]=${this.latitude}`,
                `sort[distance][longitude]=${this.longitude}`,
                `sort[distance][order]=ASC`
            ];

            if (search && search.length) {

                params.push(...[
                    `label[lk]=${search}`
                ]);
            }

            const headers: AdditionalHeader[] = [{name: DEACTIVATE_LOADER_HEADER_IDENTIFIER, value: 1}]

            return this._transportService.getPaginationItemsAPI(params, headers).pipe(map((pagination: Pagination<Transport>): Transport[] => {

                const currentTransport: Transport = this.offerLocationTransportControl.get('transport').value;

                if(!currentTransport || !currentTransport.id){

                    return pagination.items;
                }

                const isCurrentTransportDefinedIsSource: boolean = pagination.items.some((item: Transport): boolean => {

                    return item.id === currentTransport.id;
                });

                if(isCurrentTransportDefinedIsSource){

                    return pagination.items;
                }

                return [currentTransport, ...pagination.items];
            }));
        }
    }

    get selectOptionActionLabel(): string {

        switch (this.type){

            case 'train-station':

                return 'transport.trainStation.recommended.value';

            case 'airport':

                return 'transport.airport.recommended.value';

            default:

                return 'selectSearch.option.select.action.value';
        }
    }
}
